/**
 * Local persistence for an in-progress report edit.
 *
 * Editing a report builds one revision's worth of changes in memory and only
 * writes on Save. That is the right transaction boundary — but it meant an
 * accidental navigation, a closed tab or a refresh threw the whole session
 * away, on a screen where a reviewer may have worked through dozens of
 * clauses. The draft is mirrored to localStorage as it changes and offered
 * back on return.
 *
 * Deliberately NOT a sync mechanism: the draft is per-browser, never sent
 * anywhere, and is discarded the moment the revision is saved. It exists so
 * that closing a laptop lid is not destructive, nothing more.
 *
 * Every access is wrapped: localStorage throws in private mode and when the
 * quota is full, and a failure to cache a draft must never break the editor
 * the reviewer is actually using.
 */

import { HardwareCheckSelectionSchema } from '@cer-autofill/shared';
import type { FieldReport, HardwareCheckSelection } from '@cer-autofill/shared';

const PREFIX = 'cer-autofill:report-draft:';

/** Drafts older than this are ignored — a stale one is more confusing than none. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60_000;

export interface StoredDraft {
  readonly savedAt: string;
  readonly note: string;
  readonly hardwareChecks?: HardwareCheckSelection[];
  /** Automatic photo reviews the reviewer dismissed during this edit. */
  readonly dismissedPhotoReviewIds?: string[];
  readonly submission: FieldReport;
}

function dismissedReviewIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && /^[a-f0-9]{64}$/.test(id));
}

function keyFor(jobId: string, submittedAt: string): string {
  return `${PREFIX}${jobId}:${submittedAt}`;
}

export function saveDraft(
  jobId: string,
  submittedAt: string,
  draft: {
    submission: FieldReport;
    note: string;
    hardwareChecks?: HardwareCheckSelection[];
    dismissedPhotoReviewIds?: readonly string[];
  },
): void {
  try {
    const payload: StoredDraft = {
      savedAt: new Date().toISOString(),
      note: draft.note,
      ...(draft.hardwareChecks ? { hardwareChecks: draft.hardwareChecks } : {}),
      ...(draft.dismissedPhotoReviewIds?.length
        ? { dismissedPhotoReviewIds: [...draft.dismissedPhotoReviewIds] }
        : {}),
      submission: draft.submission,
    };
    localStorage.setItem(keyFor(jobId, submittedAt), JSON.stringify(payload));
  } catch {
    // Private mode or quota. The in-memory draft is unaffected.
  }
}

export function loadDraft(jobId: string, submittedAt: string): StoredDraft | null {
  try {
    const raw = localStorage.getItem(keyFor(jobId, submittedAt));
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    // Shape-check the two fields we rely on before handing it back; a draft
    // written by an older build should be ignored, not crash the editor.
    if (typeof parsed?.savedAt !== 'string' || typeof parsed?.submission !== 'object') return null;
    if (Date.now() - Date.parse(parsed.savedAt) > DRAFT_MAX_AGE_MS) {
      clearDraft(jobId, submittedAt);
      return null;
    }
    const checked = HardwareCheckSelectionSchema.array().safeParse(parsed.hardwareChecks ?? []);
    return {
      ...parsed,
      hardwareChecks: checked.success ? checked.data : [],
      dismissedPhotoReviewIds: dismissedReviewIds(parsed.dismissedPhotoReviewIds),
    };
  } catch {
    return null;
  }
}

export function clearDraft(jobId: string, submittedAt: string): void {
  try {
    localStorage.removeItem(keyFor(jobId, submittedAt));
  } catch {
    // Nothing to do — a draft we cannot remove is bounded by DRAFT_MAX_AGE_MS.
  }
}
